import React from 'react';
import { InputNumber } from 'antd';
import type { InternalMedicineData } from '../../hooks/useConsultationForm';

interface Props {
  internalMedicineData: InternalMedicineData;
  setInternalMedicineField: (section: keyof InternalMedicineData, field: string, value: any) => void;
  patientSex: 'male' | 'female' | null;
}

const CRITERIA_CONFIG = [
  {
    id: 'waist',
    label: 'Perímetro abdominal elevado',
    detail: '(>102cm H / >88cm M)',
    checkKey: 'waist_high' as const,
  },
  {
    id: 'triglycerides',
    label: 'Triglicéridos ≥150',
    detail: '',
    checkKey: 'triglycerides_high' as const,
  },
  {
    id: 'hdl',
    label: 'HDL bajo',
    detail: '(<40 H / <50 M)',
    checkKey: 'hdl_low' as const,
  },
  {
    id: 'blood_pressure',
    label: 'PA ≥130/85',
    detail: '',
    checkKey: 'bp_high' as const,
  },
  {
    id: 'fasting_glucose',
    label: 'Glucemia ≥100',
    detail: '',
    checkKey: 'fasting_glucose_high' as const,
  },
] as const;

const MetabolicSyndrome: React.FC<Props> = ({ internalMedicineData, setInternalMedicineField, patientSex }) => {
  const ms = internalMedicineData.metabolic_syndrome;
  const set = (field: string, value: any) => setInternalMedicineField('metabolic_syndrome', field, value);

  return (
    <div className="card mb-3">
      <div className="card-body">
        <h5 className="card-title">Síndrome Metabólico</h5>
        <div className="row mt-3">
          <div className="col-md-4">
            <div className="mb-3">
              <label className="form-label">Perímetro Abdominal (cm)</label>
              <InputNumber
                className="w-100"
                min={0}
                value={ms.waist_circumference_cm}
                onChange={(v) => set('waist_circumference_cm', v)}
                placeholder="cm"
              />
            </div>
          </div>
        </div>

        <div className="row mt-2">
          <div className="col-12">
            <h6 className="text-muted fw-semibold">Criterios ATP III</h6>
            <div className="d-flex flex-wrap gap-3">
              {CRITERIA_CONFIG.map((criterion) => {
                const checked = ms[criterion.checkKey] ?? false;
                return (
                  <div className="form-check" key={criterion.id}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id={`ms-crit-${criterion.id}`}
                      checked={checked}
                      disabled
                    />
                    <label className="form-check-label" htmlFor={`ms-crit-${criterion.id}`}>
                      {criterion.label}{' '}
                      {criterion.detail && <span className="text-muted small">{criterion.detail}</span>}
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="row mt-3">
          <div className="col-12">
            <div className="d-flex align-items-center gap-3">
              <span className="fw-semibold">
                {ms.criteria_positive_count}/5 criterios positivos
              </span>
              {ms.diagnosis ? (
                <span
                  style={{
                    display: 'inline-block',
                    padding: '4px 12px',
                    borderRadius: 6,
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#fff',
                    background: '#dc2626',
                  }}
                >
                  Síndrome Metabólico: POSITIVO
                </span>
              ) : ms.criteria_positive_count > 0 ? (
                <span
                  style={{
                    display: 'inline-block',
                    padding: '4px 12px',
                    borderRadius: 6,
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#fff',
                    background: '#10b981',
                  }}
                >
                  Síndrome Metabólico: NEGATIVO
                </span>
              ) : null}
            </div>
          </div>
        </div>

        <div className="row mt-2">
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">Observaciones</label>
              <textarea
                className="form-control"
                rows={2}
                value={ms.observations}
                onChange={(e) => set('observations', e.target.value)}
                placeholder="Notas adicionales sobre síndrome metabólico..."
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MetabolicSyndrome;
